import { computed, ref, watch } from 'vue'
import { useField, defineRule } from 'vee-validate'
import type { FieldContext } from 'vee-validate'

// Define website validator
defineRule('website', (value: string) => {
  if (!value) return true // Optional field
  try {
    new URL(value)
    return true
  } catch (error) {
    return 'Please enter a valid website URL (e.g., https://example.com)'
  }
})

// URL validation (alias for website)
defineRule('url', (value: string) => {
  if (!value) return true // Optional field
  try {
    new URL(value)
    return true
  } catch (error) {
    return 'Please enter a valid URL (e.g., https://example.com)'
  }
})

// GSTIN validation (15 characters: 2 digits + 10 alphanumeric + 1 check digit + Z + 1 alphanumeric)
defineRule('gstin', (value: string) => {
  if (!value) return true
  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
  return gstinRegex.test(value.toUpperCase()) || 'Please enter a valid GSTIN (15 characters)'
})

// PAN validation (10 characters: 5 letters + 4 digits + 1 letter)
defineRule('pan', (value: string) => {
  if (!value) return true
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/
  return panRegex.test(value.toUpperCase()) || 'Please enter a valid PAN (e.g., ABCDE1234F)'
})

// TAN validation (10 characters: 4 letters + 5 digits + 1 letter)
defineRule('tan', (value: string) => {
  if (!value) return true
  const tanRegex = /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/
  return tanRegex.test(value.toUpperCase()) || 'Please enter a valid TAN (e.g., ABCD12345E)'
})

// IFSC validation (11 characters: 4 letters + 0 + 6 alphanumeric)
defineRule('ifsc', (value: string) => {
  if (!value) return true
  const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/
  return ifscRegex.test(value.toUpperCase()) || 'Please enter a valid IFSC code (11 characters)'
})

// SWIFT validation (8 or 11 characters)
defineRule('swift', (value: string) => {
  if (!value) return true

  const upperValue = value.toUpperCase()

  // Check length first
  if (upperValue.length < 8) {
    return 'SWIFT code must be at least 8 characters long'
  }

  if (upperValue.length > 11) {
    return 'SWIFT code cannot be more than 11 characters long'
  }

  if (upperValue.length !== 8 && upperValue.length !== 11) {
    return 'SWIFT code must be exactly 8 or 11 characters long'
  }

  // Check bank code (first 4 characters - must be letters)
  if (!/^[A-Z]{4}/.test(upperValue)) {
    return 'First 4 characters must be letters (bank code)'
  }

  // Check country code (characters 5-6 - must be letters)
  if (!/^[A-Z]{4}[A-Z]{2}/.test(upperValue)) {
    return 'Characters 5-6 must be letters (country code)'
  }

  // Check location code (characters 7-8 - letters or numbers)
  if (!/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}/.test(upperValue)) {
    return 'Characters 7-8 must be letters or numbers (location code)'
  }

  // If 11 characters, check branch code (characters 9-11 - letters or numbers)
  if (upperValue.length === 11 && !/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}[A-Z0-9]{3}$/.test(upperValue)) {
    return 'Characters 9-11 must be letters or numbers (branch code)'
  }

  // Final validation
  const swiftRegex = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/
  return swiftRegex.test(upperValue) || 'Please enter a valid SWIFT code'
})

// Postal code validation (basic - can be enhanced for country-specific)
defineRule('postalcode', (value: string) => {
  if (!value || value.trim() === '') return true // Only validate if there's a value
  // Basic validation - 6 digits for India, can be extended for other countries
  const postalRegex = /^[1-9][0-9]{5}$/
  return postalRegex.test(value) || 'Please enter a valid postal code'
})

// Credit period validation (integer between 0-365)
defineRule('creditperiod', (value: string | number) => {
  if (!value) return true
  const num = Number(value)
  if (isNaN(num) || !Number.isInteger(num)) {
    return 'Credit period must be a whole number'
  }
  if (num < 0 || num > 365) {
    return 'Credit period must be between 0 and 365 days'
  }
  return true
})

// Positive number validation
defineRule('positive', (value: string | number) => {
  if (!value) return true
  const num = Number(value)
  if (isNaN(num)) {
    return 'Please enter a valid number'
  }
  if (num < 0) {
    return 'Value must be greater than 0'
  }
  return true
})

// Alphabetic validation (only letters and spaces)
defineRule('alpha', (value: string) => {
  if (!value) return true
  const alphaRegex = /^[A-Za-z\s]+$/
  return alphaRegex.test(value) || 'Please enter only letters and spaces'
})

// Alphabet validation (only letters and spaces, no numbers)
defineRule('alphabet', (value: string) => {
  if (!value) return true
  const alphabetRegex = /^[A-Za-z\s]+$/
  return alphabetRegex.test(value) || 'Please enter only letters and spaces, numbers are not allowed'
})

// Numeric validation (numbers with optional decimal point and negative sign)
defineRule('numeric', (value: string | number) => {
  if (!value) return true
  // Allow integers, decimals, and negative numbers
  const numericRegex = /^-?[0-9]+(\.[0-9]+)?$/
  return numericRegex.test(String(value)) || 'Please enter a valid number'
})

// Alphanumeric validation (letters, numbers, and spaces)
defineRule('alphanumeric', (value: string) => {
  if (!value) return true
  const alphanumericRegex = /^[A-Za-z0-9\s]+$/
  return alphanumericRegex.test(value) || 'Please enter only letters, numbers, and spaces'
})

// Percentage validation (0-100)
defineRule('percentage', (value: string | number) => {
  if (!value) return true
  const num = Number(value)
  if (isNaN(num)) {
    return 'Please enter a valid number'
  }
  if (num > 100) {
    return 'Value cannot be greater than 100%'
  }
  return true
})

type InputValue = string | number | boolean | Array<any> | null | undefined
type ValidationTypes = 'email' | 'phone' | 'password' | 'url' | 'numeric' | 'alpha' | 'alphanumeric' | 'alphabet' | 'website' | 'gstin' | 'pan' | 'tan' | 'ifsc' | 'swift' | 'postalcode' | 'creditperiod' | 'positive' | 'percentage'

interface UseVeeValidateProps {
    name: string
    modelValue?: InputValue
    required?: boolean
    validationType?: ValidationTypes
    min?: number
    max?: number
    minLength?: number
    maxLength?: number
    customRules?: Record<string, (value: any) => boolean | string>
}

export function useVeeValidate(props: UseVeeValidateProps) {
    const touched = ref(false)
    const validated = ref(false)

    const validationRules = computed(() => {
        const rules: string[] = []

        // Required validation
        if (props.required) {
            rules.push('required')
        }

        // Type-specific validation
        if (props.validationType) {
            rules.push(props.validationType)
        }

        // Numeric validations
        if (typeof props.min === 'number') {
            rules.push(`min:${props.min}`)
        }
        if (typeof props.max === 'number') {
            rules.push(`max:${props.max}`)
        }

        // String length validations
        if (typeof props.minLength === 'number') {
            rules.push(`min_length:${props.minLength}`)
        }
        if (typeof props.maxLength === 'number') {
            rules.push(`max_length:${props.maxLength}`)
        }

        // Custom rules
        if (props.customRules) {
            Object.entries(props.customRules).forEach(([ruleName, ruleFunction]) => {
                rules.push(ruleName)
            })
        }

        return rules.join('|')
    })

    // Handle different types of initial values
    const getInitialValue = () => {
        if (props.modelValue === undefined || props.modelValue === null) {
            // Default values based on common input types
            if (Array.isArray(props.modelValue)) {
                return [] // For multiselect, checkbox groups
            } else if (typeof props.modelValue === 'boolean') {
                return false // For checkbox, switch
            } else {
                return '' // For text inputs, textarea, etc.
            }
        }
        return props.modelValue
    }

    const { value: fieldValue, errorMessage, meta, validate } = useField(() => props.name || 'default-field', validationRules, {
        initialValue: getInitialValue(),
        validateOnMount: false,
        validateOnValueUpdate: false,
        ...(props.customRules && { validateFnMap: props.customRules })
    })

    const shouldShowError = computed(() => {
        // Don't show error if field has a value but hasn't been touched by user
        if (!touched.value && !validated.value && !isEmpty(fieldValue.value)) {
            return false
        }
        // Only show error if the field has been touched (blurred) or form submission was attempted
        return (touched.value && errorMessage.value) || (validated.value && errorMessage.value)
    })

    const handleValidation = async () => {
        touched.value = true
        await validate()
    }

    // Validate when form submission is attempted
    watch(() => meta.validated, async (isValidated) => {
        validated.value = isValidated
        if (isValidated) {
            await validate()
        }
    })

    // Helper function to check if the value is empty
    const isEmpty = (value: any): boolean => {
        if (value === null || value === undefined) return true
        if (typeof value === 'string') return value.trim() === ''
        if (Array.isArray(value)) return value.length === 0
        if (typeof value === 'object') return Object.keys(value).length === 0
        return false
    }

    // Helper function to format value based on type
    const formatValue = (value: any) => {
        if (isEmpty(value)) return getInitialValue()
        if (Array.isArray(value)) return [...value]
        if (typeof value === 'object') return { ...value }
        return value
    }

    // Update field value with proper formatting
    const updateValue = (newValue: any) => {
        fieldValue.value = formatValue(newValue)
    }

    // Reset validation state
    const resetValidation = () => {
        touched.value = false
        validated.value = false
    }

    return {
        fieldValue,
        errorMessage,
        touched,
        shouldShowError,
        validate,
        handleValidation,
        updateValue,
        isEmpty,
        resetValidation
    }
}
